import { ProcessSubscriptionResponse } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionResponse';
import {
  bidTemplatesParametersFromUnknown,
  buildBidTemplatesParameters,
  buildCatchUpParameters,
  buildProbeParameters,
  BID_TEMPLATES_DEFAULT_BUCKET,
  BID_TEMPLATES_DEFAULT_FROM,
  BID_TEMPLATES_DEFAULT_LOCALE,
  BID_TEMPLATES_DEFAULT_TO,
  CATCH_UP_DEFAULT_TAKE,
  catchUpTakeFromUnknown,
  farmerReportsParametersFromUnknown,
  isBidTemplatesProcess,
  isCatchUpPostProcessingProcess,
  isFarmerReportsProcess,
  isOrchestrationProbeProcess,
  isSatelliteCatalogueProcess,
  PROBE_DEFAULT_FAIL_EVERY,
  satellitePipelineParametersTemplate,
  probeFailEveryFromUnknown,
} from '@app/pages/admin/assignment-start.util';
import { isGeometryProcessKey } from '@app/pages/admin/orchestration-process-keys.util';
import { defaultOrchestrationTimeZoneId } from '@app/pages/admin/orchestration-time-zones.util';

export type SubscriptionFormMode = 'create' | 'edit' | 'copy';

export interface SubscriptionProcessOption {
  id: string;
  label: string;
}

export interface SubscriptionFormValue {
  name: string;
  processDefinitionKey: string;
  cronExpression: string;
  timeZoneId: string;
  parametersJson: string;
  isActive: boolean;
}

export function emptySubscriptionForm(): SubscriptionFormValue {
  return {
    name: '',
    processDefinitionKey: '',
    cronExpression: '0 0 6 * * ?',
    timeZoneId: defaultOrchestrationTimeZoneId(),
    parametersJson: '{}',
    isActive: true,
  };
}

export function subscriptionFormFromItem(item: ProcessSubscriptionResponse): SubscriptionFormValue {
  return {
    name: item.name ?? '',
    processDefinitionKey: item.processDefinitionKey ?? '',
    cronExpression: item.cronExpression ?? '',
    timeZoneId: item.timeZoneId ?? defaultOrchestrationTimeZoneId(),
    parametersJson: formatReconstructedParameters(item.processDefinitionKey, item.safeInputJson),
    isActive: item.isActive ?? false,
  };
}

export function subscriptionCopyForm(
  item: ProcessSubscriptionResponse,
  copyName: string,
): SubscriptionFormValue {
  return {
    ...subscriptionFormFromItem(item),
    name: copyName,
    isActive: false,
  };
}

export function isBlankParametersJson(json?: string | null): boolean {
  const raw = (json ?? '').trim();
  if (!raw) {
    return true;
  }
  const parsed = tryParseObject(raw);
  return !!parsed && Object.keys(parsed).length === 0;
}

export function subscriptionSafePreviewJson(value?: string | null): string {
  if (value == null || !value.trim()) {
    return '';
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

export function looksLikeSafeSatelliteSnapshot(value: Record<string, unknown>): boolean {
  if (hasNestedGeometry(value)) {
    return false;
  }
  return typeof readField(value, 'geometrySource', 'GeometrySource') === 'string'
    || typeof readField(value, 'regionCount', 'RegionCount') === 'number'
    || typeof readField(value, 'lookbackDays', 'LookbackDays') === 'number';
}

export function reconstructProcessParametersFromSafe(
  processKey: string,
  safeJson?: string | null,
): Record<string, unknown> | null {
  const parsed = tryParseObject(safeJson);
  if (!parsed) {
    return null;
  }
  if (hasNestedGeometry(parsed)) {
    return parsed;
  }
  if (isSatelliteCatalogueProcess(processKey)) {
    return reconstructSatelliteCatalogueParameters(processKey, parsed);
  }
  if (isFarmerReportsProcess(processKey)) {
    return farmerReportsParametersFromUnknown(parsed);
  }
  if (isBidTemplatesProcess(processKey)) {
    return bidTemplatesParametersFromUnknown(parsed);
  }
  if (isCatchUpPostProcessingProcess(processKey)) {
    return buildCatchUpParameters(catchUpTakeFromUnknown(parsed));
  }
  if (isOrchestrationProbeProcess(processKey)) {
    return buildProbeParameters(probeFailEveryFromUnknown(parsed));
  }
  return looksLikeSafeSatelliteSnapshot(parsed) ? null : parsed;
}

function formatReconstructedParameters(processKey?: string | null, safeJson?: string | null): string {
  const reconstructed = reconstructProcessParametersFromSafe(processKey ?? '', safeJson);
  return reconstructed ? JSON.stringify(reconstructed, null, 2) : '';
}

function reconstructSatelliteCatalogueParameters(
  processKey: string,
  parsed: Record<string, unknown>,
): Record<string, unknown> | null {
  const sourceType = readField(parsed, 'geometrySource', 'GeometrySource');
  if (typeof sourceType !== 'string' || !sourceType.trim()) {
    return null;
  }
  if (sourceType === 'GeoJson' || sourceType === 'GeoJsonFile') {
    return null;
  }

  const geometry: Record<string, unknown> = { sourceType };
  if (sourceType === 'Regions') {
    const regionIds = readField(parsed, 'regionIds', 'RegionIds');
    if (!Array.isArray(regionIds) || regionIds.length === 0) {
      return null;
    }
    geometry['regionIds'] = regionIds;
  }
  if (sourceType === 'CountryName') {
    const countryName = readField(parsed, 'countryName', 'CountryName');
    if (typeof countryName !== 'string' || !countryName.trim()) {
      return null;
    }
    geometry['countryName'] = countryName.trim();
  }

  const startDate = readField(parsed, 'startDate', 'StartDate');
  const endDate = readField(parsed, 'endDate', 'EndDate');
  if (startDate == null || endDate == null) {
    return null;
  }

  const name = readField(parsed, 'name', 'Name');
  const parameters: Record<string, unknown> = {
    name: typeof name === 'string' ? name : '',
    geometry,
    providers: readField(parsed, 'providers', 'Providers') ?? 'Sentinel2',
    startDate,
    endDate,
    priority: readField(parsed, 'priority', 'Priority') ?? 'Normal',
  };

  if (processKey === 'satellite.find-products') {
    const lookback = readField(parsed, 'lookback', 'Lookback');
    const lookbackDays = readField(parsed, 'lookbackDays', 'LookbackDays');
    parameters['lookback'] = typeof lookback === 'string' && lookback.trim()
      ? lookback
      : formatLookback(typeof lookbackDays === 'number' ? lookbackDays : 2);
  }
  if (processKey === 'satellite.download-found-products') {
    const cloudCover = readField(parsed, 'maximumCloudCover', 'MaximumCloudCover');
    if (typeof cloudCover === 'number') {
      parameters['maximumCloudCover'] = cloudCover;
    }
  }
  return parameters;
}

function formatLookback(totalDays: number): string {
  if (!Number.isFinite(totalDays)) {
    return '2.00:00:00';
  }
  const sign = totalDays < 0 ? '-' : '';
  const abs = Math.abs(totalDays);
  const days = Math.trunc(abs);
  const totalSeconds = Math.round((abs - days) * 86400);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${sign}${days}.${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function tryParseObject(json?: string | null): Record<string, unknown> | null {
  if (json == null || !json.trim()) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(json);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function hasNestedGeometry(value: Record<string, unknown>): boolean {
  return isRecord(value['geometry']) || isRecord(value['Geometry']);
}

function readField(obj: Record<string, unknown>, ...names: string[]): unknown {
  for (const name of names) {
    const value = obj[name];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function toSubscriptionProcessOptions(
  processes: Array<{ key: string; version?: number }>,
): SubscriptionProcessOption[] {
  return processes.map(process => ({
    id: process.key,
    label: process.version == null ? process.key : `${process.key} (v${process.version})`,
  }));
}

export function subscriptionParametersTemplate(processKey: string): string {
  if (processKey === 'satellite.find-products') {
    return `{
  "name": "Scheduled satellite search",
  "geometry": {
    "sourceType": "CountryBoundaries"
  },
  "providers": "All",
  "priority": "Normal",
  "startDate": "{{today-7d}}",
  "endDate": "{{today}}",
  "lookback": "2.00:00:00"
}`;
  }
  if (processKey === 'satellite.download-found-products') {
    return `{
  "name": "Scheduled satellite download",
  "geometry": {
    "sourceType": "CountryBoundaries"
  },
  "providers": "All",
  "priority": "Normal",
  "startDate": "{{today-7d}}",
  "endDate": "{{today}}",
  "maximumCloudCover": 100
}`;
  }
  if (processKey === 'satellite.create-product-postprocessing') {
    return `{
  "satelliteProductId": "00000000-0000-0000-0000-000000000000",
  "priority": "Normal"
}`;
  }
  if (isGeometryProcessKey(processKey)) {
    return `{
  "sourceType": "CountryBoundaries"
}`;
  }
  if (isFarmerReportsProcess(processKey)) {
    return JSON.stringify(farmerReportsParametersFromUnknown(null), null, 2);
  }
  if (isBidTemplatesProcess(processKey)) {
    return JSON.stringify(buildBidTemplatesParameters(
      BID_TEMPLATES_DEFAULT_FROM,
      BID_TEMPLATES_DEFAULT_TO,
      BID_TEMPLATES_DEFAULT_LOCALE,
      BID_TEMPLATES_DEFAULT_BUCKET,
    ), null, 2);
  }
  if (isCatchUpPostProcessingProcess(processKey)) {
    return JSON.stringify(buildCatchUpParameters(CATCH_UP_DEFAULT_TAKE), null, 2);
  }
  if (isOrchestrationProbeProcess(processKey)) {
    return JSON.stringify(buildProbeParameters(PROBE_DEFAULT_FAIL_EVERY), null, 2);
  }
  return satellitePipelineParametersTemplate(processKey) ?? '{}';
}
